import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import LoanLink from './LoanLink';
import ClientLink from './ClientLink';
import { formatDateDMY } from '../utils/dateUtils';
import { toCurrency } from '../utils/adminMetrics';

// Loans to consider writing off (GET /api/loans/write-off-candidates): the
// API applies the write-off policy and the prescription rules; this lists
// each loan with its reasons as tags. Nothing is written off here by itself:
// "Write off" opens the page's own write-off dialog with the reason note
// pre-filled from the tags, which the staff member can edit or cancel.
// Staff can also record an acknowledgement of debt or a summons, which
// interrupts prescription.

const TAG = {
  prescribed: ['Prescribed', 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800'],
  prescription_soon: ['Prescribes soon', 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800'],
  policy: ['Policy', 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600'],
};

const EVENT_TYPES = [
  ['AcknowledgementOfDebt', 'Acknowledgement of debt'],
  ['Summons', 'Summons'],
];

/** The write-off reason note, from the candidate's reasons. */
export const writeOffNoteFor = (c) => `Write-off candidate (${(c.reasons || []).map((r) => TAG[r.code]?.[0] || r.code).join(', ')}): ${(c.reasons || []).map((r) => r.text).join(' ')}`;

const WriteOffCandidatesPanel = ({ canWriteOff = false, onWriteOff, refreshKey = 0, className = '' }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [eventFor, setEventFor] = useState(null); // loan id
  const [eventType, setEventType] = useState(EVENT_TYPES[0][0]);
  const [eventText, setEventText] = useState('');
  const [eventBusy, setEventBusy] = useState(false);
  const [eventError, setEventError] = useState('');

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(apiUrl('/api/loans/write-off-candidates'), { headers: auth() });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 403) { setData({ candidates: [] }); return; }
      setError(err?.response?.data?.error || 'The write-off candidates could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const saveEvent = async (loanId) => {
    if (!eventText.trim()) { setEventError('Write what happened.'); return; }
    setEventBusy(true);
    setEventError('');
    try {
      await axios.post(apiUrl(`/api/loans/${loanId}/notes`), { note_type: eventType, note_text: eventText.trim() }, { headers: auth() });
      setEventFor(null);
      setEventText('');
      await load();
    } catch (err) {
      setEventError(err?.response?.data?.error || 'The note could not be saved.');
    } finally {
      setEventBusy(false);
    }
  };

  const list = data?.candidates || [];
  if (!loading && !error && data && list.length === 0) return null;

  return (
    <section className={`bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden ${className}`}>
      <div className="h-1 bg-red-500" />
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 flex items-center justify-between text-left border-b border-gray-100 dark:border-slate-700">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">
            Write-off candidates{list.length ? ` (${list.length})` : ''}
          </h3>
          <p className="text-[11px] text-gray-500 dark:text-slate-400">
            Loans flagged by the write-off policy or prescription. Nothing is written off automatically.
          </p>
        </div>
        <span className="text-xs text-gray-500 dark:text-slate-400">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="p-4">
          {error && <div className="mb-3 p-2 rounded border border-red-200 bg-red-50 text-red-700 text-xs dark:bg-red-900/30 dark:border-red-800 dark:text-red-300">{error}</div>}
          {loading && !data ? (
            <div className="text-sm text-gray-500 dark:text-slate-400">Loading…</div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-slate-700">
              {list.map((c) => (
                <li key={c.loan_id} className="py-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <LoanLink loanId={c.loan_id} className="font-semibold">{c.loan_code}</LoanLink>
                        <ClientLink clientId={c.client_id} name={c.client_name} className="text-sm" />
                        <span className="text-[11px] text-gray-500 dark:text-slate-400">{c.status}</span>
                        {c.reasons.map((r) => (
                          <span key={r.code} title={r.text}
                            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${TAG[r.code]?.[1] || TAG.policy[1]}`}>
                            {TAG[r.code]?.[0] || r.code}
                          </span>
                        ))}
                      </div>
                      <div className="mt-1 grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-0.5 text-xs text-gray-600 dark:text-slate-300">
                        <span>Balance <b className="text-gray-900 dark:text-slate-100">{toCurrency(c.gross_balance)}</b></span>
                        <span>{c.days_past_due} days past due</span>
                        <span>Last paid {c.last_payment_date ? formatDateDMY(c.last_payment_date) : 'never'}</span>
                        <span>Prescription from {c.prescription_start ? formatDateDMY(c.prescription_start) : '-'}</span>
                        <span>Prescribes {c.prescription_date ? formatDateDMY(c.prescription_date) : '-'}</span>
                      </div>
                      <ul className="mt-1 text-[11px] text-gray-500 dark:text-slate-400 list-disc pl-4">
                        {c.reasons.map((r) => <li key={r.code}>{r.text}</li>)}
                      </ul>
                    </div>
                    {canWriteOff && (
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => { setEventFor(eventFor === c.loan_id ? null : c.loan_id); setEventError(''); }}
                          className="px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700">
                          Record acknowledgement / summons
                        </button>
                        <button type="button" onClick={() => onWriteOff && onWriteOff(c, writeOffNoteFor(c))}
                          className="px-3 py-1.5 rounded-md text-xs font-semibold bg-red-50 text-red-800 border border-red-200 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
                          Write off
                        </button>
                      </div>
                    )}
                  </div>
                  {eventFor === c.loan_id && (
                    <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 dark:border-slate-700 p-2">
                      <label className="text-xs text-gray-700 dark:text-slate-300">
                        Event
                        <select value={eventType} onChange={(e) => setEventType(e.target.value)}
                          className="mt-1 block">
                          {EVENT_TYPES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                        </select>
                      </label>
                      <label className="text-xs text-gray-700 dark:text-slate-300 flex-1 min-w-[12rem]">
                        What happened (dated today)
                        <input value={eventText} onChange={(e) => setEventText(e.target.value)} placeholder="e.g. Client signed an acknowledgement of debt"
                          className="mt-1 block w-full" />
                      </label>
                      <button type="button" disabled={eventBusy} onClick={() => saveEvent(c.loan_id)}
                        className="px-3 py-1.5 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                        {eventBusy ? 'Saving…' : 'Save'}
                      </button>
                      {eventError && <div className="w-full text-xs text-red-600 dark:text-red-400">{eventError}</div>}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};

export default WriteOffCandidatesPanel;
